/**
 * WCC360 v2 — free taster.
 *
 * Eight questions lifted verbatim from the real assessment, with the same
 * archetype weights the full walk uses, scored through the same
 * `addSignal` tally. Nothing psychometric is invented here: every item id,
 * every label and every weight below is the one used on the corresponding
 * Part 2 screen, so a taster result can never contradict the full result —
 * it is simply a much thinner slice of evidence.
 *
 * Only preference and situation items are used. No virtue Likert, ever.
 */
import {
  ARCHETYPES, addSignal, emptyVector,
  type Archetype, type ArchetypeVector,
} from "./archetypes";
import { ARCHETYPE_TAGLINES } from "./results";

export interface TasterOption {
  id: string;
  label: string;
  /** Same deltas as the full assessment. Empty = a genuine "none of these". */
  archetype: Partial<Record<Archetype, number>>;
}

export interface TasterItem {
  /** Canonical screen id in the full assessment. */
  id: string;
  eyebrow: string;
  question: string;
  helper: string;
  /** Signal weight this screen carries in the full engine. */
  weight: number;
  options: TasterOption[];
}

export const TASTER_ITEMS: TasterItem[] = [
  {
    id: "p2_workday_sort",
    eyebrow: "Last week",
    question: "What actually took up most of your week?",
    helper: "What happened, not what you meant to do.",
    weight: 1,
    options: [
      { id: "building", label: "Building or fixing something with my hands", archetype: { Builder: 3 } },
      { id: "thinking", label: "Reading, analysing, working out a hard problem", archetype: { Thinker: 3 } },
      { id: "making", label: "Making something — writing, designing, filming", archetype: { Creator: 3 } },
      { id: "talking", label: "Talking to people — customers, clients, strangers", archetype: { Connector: 3 } },
      { id: "organising", label: "Organising, scheduling, keeping things on track", archetype: { Organizer: 3 } },
      { id: "leading", label: "Deciding what a group of people should do next", archetype: { Leader: 3 } },
      { id: "helping", label: "Helping someone through something difficult", archetype: { Caregiver: 3 } },
      { id: "arguing", label: "Standing up for someone or making a case", archetype: { Advocate: 3 } },
    ],
  },
  {
    id: "p2_bad_day",
    eyebrow: "Nobody's watching",
    question: "What would you still find worth doing on a bad day, when no one is watching?",
    helper: "First instinct.",
    weight: 1,
    options: [
      { id: "help", label: "Helping or fixing something for someone", archetype: { Caregiver: 1 } },
      { id: "puzzle", label: "Solving a hard puzzle", archetype: { Thinker: 1 } },
      { id: "make", label: "Making something beautiful or original", archetype: { Creator: 1 } },
      { id: "win", label: "Winning, or hitting a goal", archetype: { Leader: 1 } },
      { id: "build", label: "Building something that lasts", archetype: { Builder: 1 } },
    ],
  },
  {
    id: "p2_ten_years",
    eyebrow: "Ten years from now",
    question: "If ten years from now you looked back proud, which of these got you there?",
    helper: "Pick the one that fits first.",
    weight: 1.2,
    options: [
      { id: "built", label: "Built something people can point to", archetype: { Builder: 1 } },
      { id: "cracked", label: "Solved a problem people said was unsolvable", archetype: { Thinker: 1 } },
      { id: "moved", label: "Made something that moved people", archetype: { Creator: 1 } },
      { id: "lifted", label: "Known for lifting the people around me", archetype: { Caregiver: 1 } },
      { id: "led", label: "In the room where the decisions get made", archetype: { Leader: 1 } },
      { id: "changed", label: "Changed how something in the world runs", archetype: { Advocate: 1 } },
    ],
  },
  {
    id: "p2_scenario_month_off",
    eyebrow: "A whole month",
    question: "A month off, fully paid. Nobody expects anything.",
    helper: "What honestly ends up filling it? First instinct.",
    weight: 0.8,
    options: [
      { id: "build", label: "Build or fix something I've been putting off", archetype: { Builder: 1 } },
      { id: "learn", label: "Deep-dive a subject I've been circling", archetype: { Thinker: 1 } },
      { id: "make", label: "Make something — writing, film, music, design", archetype: { Creator: 1 } },
      { id: "travel", label: "Travel — new places, new people", archetype: { Connector: 1 } },
      { id: "volunteer", label: "Volunteer somewhere that matters to me", archetype: { Advocate: 0.6, Caregiver: 0.6 } },
    ],
  },
  {
    id: "p2_scenario_open_week",
    eyebrow: "A blank week",
    question: "A completely open work week. No obligations.",
    helper: "What actually fills it — not the wise-sounding answer.",
    weight: 0.8,
    options: [
      { id: "make", label: "Making something new", archetype: { Creator: 1 } },
      { id: "crack", label: "Cracking a problem I keep circling", archetype: { Thinker: 1 } },
      { id: "meet", label: "Meeting people who do what I want to do", archetype: { Connector: 1 } },
      { id: "order", label: "Getting my life in order — plans, systems, admin", archetype: { Organizer: 1 } },
    ],
  },
  {
    id: "p2_scenario_teammate",
    eyebrow: "Something everyone can see",
    question: "Someone on your team is quietly falling behind.",
    helper: "It's obvious. Nobody's saying it out loud. What do you actually do first?",
    weight: 0.7,
    options: [
      { id: "aside", label: "Pull them aside — see what's actually going on", archetype: { Caregiver: 1 } },
      { id: "cover", label: "Quietly cover for them until they're back on it", archetype: { Caregiver: 0.5, Connector: 0.5 } },
      { id: "loop", label: "Loop in the manager — this isn't mine to fix", archetype: { Leader: 0.7, Organizer: 0.3 } },
      { id: "focus", label: "Focus on my own work — they'll sort it or they won't", archetype: {} },
    ],
  },
  {
    id: "p2_whose_problem",
    eyebrow: "Whose problem",
    question: "Whose problem would you most want to spend your working life on?",
    helper: "The one you'd stay late for.",
    weight: 0.6,
    options: [
      { id: "care", label: "People who are unwell or in need of care", archetype: { Caregiver: 1 } },
      { id: "grow", label: "People and businesses trying to build and grow", archetype: {} },
      { id: "planet", label: "The planet and the future", archetype: { Advocate: 1 } },
      { id: "learn", label: "People who want to learn, or to be moved by something", archetype: {} },
      { id: "systems", label: "Systems and communities that need to work better", archetype: { Organizer: 1 } },
    ],
  },
  {
    id: "p2_scenario_failure",
    eyebrow: "Six months later",
    question: "A project you led just failed. Publicly. Where are you six months on?",
    helper: "Honestly, not heroically.",
    weight: 0.6,
    options: [
      { id: "next", label: "Running the next attempt — sharper this time", archetype: { Leader: 0.6, Builder: 0.6 } },
      { id: "under", label: "Working under someone I trust, learning fast", archetype: { Thinker: 0.7 } },
      { id: "stable", label: "In a stable role while I decide what's next", archetype: {} },
      { id: "different", label: "Doing something completely different", archetype: { Creator: 0.6, Advocate: 0.4 } },
    ],
  },
];

export type TasterAnswers = Record<string, string>;

export interface TasterResult {
  primary: Archetype;
  secondary: Archetype;
  primaryTagline: string;
  secondaryTagline: string;
  vector: ArchetypeVector;
  /** 0..100 shares, highest first. */
  rows: Array<{ name: Archetype; share: number }>;
  /** The answers each leading archetype came from — evidence, never a score alone. */
  evidence: Array<{ question: string; answer: string }>;
}

/** Scores the taster with the same tally the full assessment uses. */
export function scoreTaster(answers: TasterAnswers): TasterResult {
  let vector = emptyVector();
  const evidence: Array<{ question: string; answer: string }> = [];

  for (const item of TASTER_ITEMS) {
    const choice = answers[item.id];
    if (!choice) continue;
    const option = item.options.find((o) => o.id === choice);
    if (!option) continue;
    vector = addSignal(vector, option.archetype, item.weight);
    evidence.push({ question: item.question, answer: option.label });
  }

  const total = ARCHETYPES.reduce((s, a) => s + (vector[a] ?? 0), 0);
  const rows = ARCHETYPES
    .map((name) => ({
      name,
      share: total > 0 ? Math.round(((vector[name] ?? 0) / total) * 100) : 0,
    }))
    .sort((a, b) => b.share - a.share || ARCHETYPES.indexOf(a.name) - ARCHETYPES.indexOf(b.name));

  const primary = rows[0].name;
  const secondary = rows[1].name;

  return {
    primary,
    secondary,
    primaryTagline: ARCHETYPE_TAGLINES[primary],
    secondaryTagline: ARCHETYPE_TAGLINES[secondary],
    vector,
    rows,
    evidence,
  };
}
